import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";

import type { ChatTranscriptItem, ReferencedFile } from "~/ai-chat/model/chat-message";

interface AiChatContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: ChatTranscriptItem[];
  appendMessage: (message: ChatTranscriptItem) => void;
  updateMessage: (id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => void;
  clearMessages: () => void;
  // Load a persisted conversation's messages into the transcript (FE-3).
  replaceMessages: (items: ChatTranscriptItem[]) => void;
  // Persisted-conversation id the transcript is bound to, or null for an
  // unsaved chat that hasn't been created on the backend yet.
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  startNewConversation: () => void;
  // User-facing conversation title. Editable only before the conversation is
  // created (activeConversationId === null) — used as the create title; there
  // is no rename endpoint yet, so it's read-only once persisted.
  chatTitle: string | null;
  setChatTitle: (title: string | null) => void;
  isAvailable: boolean;
  markUnavailable: () => void;
  hasMcpTools: boolean;
  markMcpToolsAvailable: () => void;
  notebookContext: string | null;
  setNotebookContext: (ctx: string | null) => void;
  onInsertCode: ((code: string) => void) | null;
  setOnInsertCode: (fn: ((code: string) => void) | null) => void;
  // Resolves extra system-prompt context for one specific request (e.g. the parameter schema of the
  // job function a prompt names). Set by the notebook host; awaited at generation time.
  resolvePromptContext: ((prompt: string) => Promise<string | null>) | null;
  setResolvePromptContext: (fn: ((prompt: string) => Promise<string | null>) | null) => void;
  referencedFiles: ReferencedFile[];
  addReferencedFiles: (files: ReferencedFile[]) => void;
  removeReferencedFile: (resourceId: string) => void;
  clearReferencedFiles: () => void;
}

const AiChatContext = React.createContext<AiChatContextValue | null>(null);

export function useAiChat() {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChat must be used within an AiChatProvider.");
  }
  return context;
}

export function useAiChatOptional() {
  return useContext(AiChatContext);
}

interface AiChatProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function AiChatProvider({ children, defaultOpen = false }: AiChatProviderProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState<ChatTranscriptItem[]>([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [hasMcpTools, setHasMcpTools] = useState(false);
  const [notebookContext, setNotebookContext] = useState<string | null>(null);
  const [onInsertCode, setOnInsertCode] = useState<((code: string) => void) | null>(null);
  const [resolvePromptContext, setResolvePromptContext] = useState<((prompt: string) => Promise<string | null>) | null>(
    null,
  );
  const [referencedFiles, setReferencedFiles] = useState<ReferencedFile[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState<string | null>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const appendMessage = useCallback((message: ChatTranscriptItem) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Used by streaming to grow an in-flight assistant message as deltas arrive.
  const updateMessage = useCallback((id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => {
    setMessages((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setReferencedFiles([]);
  }, []);

  const replaceMessages = useCallback((items: ChatTranscriptItem[]) => setMessages(items), []);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setReferencedFiles([]);
    setActiveConversationId(null);
    setChatTitle(null);
  }, []);

  const markUnavailable = useCallback(() => setIsAvailable(false), []);
  const markMcpToolsAvailable = useCallback(() => setHasMcpTools(true), []);

  // useState setter for a function value requires the functional-update form to avoid
  // React treating the function itself as a state initializer.
  const handleSetOnInsertCode = useCallback((fn: ((code: string) => void) | null) => {
    setOnInsertCode(() => fn);
  }, []);

  const handleSetResolvePromptContext = useCallback((fn: ((prompt: string) => Promise<string | null>) | null) => {
    setResolvePromptContext(() => fn);
  }, []);

  const addReferencedFiles = useCallback((files: ReferencedFile[]) => {
    setReferencedFiles((prev) => {
      const byId = new Map(prev.map((file) => [file.resourceId, file]));
      for (const file of files) {
        byId.set(file.resourceId, file);
      }
      return [...byId.values()];
    });
  }, []);

  const removeReferencedFile = useCallback((resourceId: string) => {
    setReferencedFiles((prev) => prev.filter((file) => file.resourceId !== resourceId));
  }, []);

  const clearReferencedFiles = useCallback(() => setReferencedFiles([]), []);

  const value = useMemo<AiChatContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      clearMessages,
      replaceMessages,
      activeConversationId,
      setActiveConversationId,
      startNewConversation,
      chatTitle,
      setChatTitle,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      notebookContext,
      setNotebookContext,
      onInsertCode,
      setOnInsertCode: handleSetOnInsertCode,
      resolvePromptContext,
      setResolvePromptContext: handleSetResolvePromptContext,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      clearMessages,
      replaceMessages,
      activeConversationId,
      startNewConversation,
      chatTitle,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      notebookContext,
      onInsertCode,
      handleSetOnInsertCode,
      resolvePromptContext,
      handleSetResolvePromptContext,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
    ],
  );

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
}
